import type { MountainContext } from "../engine/types/MountainContext.js";
import type { AlertState } from "../engine/types/AlertState.js";
import type { GpsSnapshot } from "../engine/types/GpsSnapshot.js";
import type { RouteProgress } from "../engine/types/RouteProgress.js";
import type { WeatherSnapshot } from "../engine/types/WeatherSnapshot.js";
import type {
  TheraAlertState,
  TheraGpsState,
  TheraRouteState,
  TheraRuntimeSnapshot,
  TheraWeatherState,
} from "./TheraIntegrationInput.js";

const finite = (value: number | undefined): value is number =>
  typeof value === "number" && Number.isFinite(value);

const finiteOrNull = (value: number | null | undefined): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const nonNegativeOrNull = (value: number | null | undefined): number | null => {
  const numeric = finiteOrNull(value);
  return numeric === null ? null : Math.max(0, numeric);
};

const percentOrNull = (value: number | null | undefined): number | null => {
  const numeric = nonNegativeOrNull(value);
  return numeric === null ? null : Math.min(100, numeric);
};

const validTimestamp = (value: string | undefined, fallback: string): string =>
  typeof value === "string" && !Number.isNaN(Date.parse(value)) ? value : fallback;

const mapGps = (gps: TheraGpsState | undefined, fallbackTimestamp: string): GpsSnapshot | undefined => {
  if (!gps || !finite(gps.latitude) || !finite(gps.longitude) || !finite(gps.accuracyMeters)) return undefined;
  return {
    latitude: gps.latitude,
    longitude: gps.longitude,
    altitudeMeters: finiteOrNull(gps.altitudeMeters),
    accuracyMeters: Math.max(0, gps.accuracyMeters),
    timestamp: validTimestamp(gps.timestamp, fallbackTimestamp),
  };
};

const mapRoute = (route: TheraRouteState | undefined, fallbackTimestamp: string): RouteProgress | undefined => {
  if (!route || !finite(route.distanceTravelledMeters) || !finite(route.distanceRemainingMeters) || !finite(route.progressPercent)) {
    return undefined;
  }
  return {
    distanceTravelledMeters: Math.max(0, route.distanceTravelledMeters),
    distanceRemainingMeters: Math.max(0, route.distanceRemainingMeters),
    progressPercent: Math.min(100, Math.max(0, route.progressPercent)),
    routeDeviationMeters: nonNegativeOrNull(route.routeDeviationMeters),
    estimatedTimeRemainingMinutes: nonNegativeOrNull(route.estimatedTimeRemainingMinutes),
    timestamp: validTimestamp(route.timestamp, fallbackTimestamp),
  };
};

const mapWeather = (weather: TheraWeatherState | undefined, fallbackTimestamp: string): WeatherSnapshot | undefined => {
  if (!weather) return undefined;
  const temperature = finiteOrNull(weather.temperatureCelsius);
  const wind = nonNegativeOrNull(weather.windSpeedKph);
  const gust = nonNegativeOrNull(weather.gustSpeedKph);
  const precipitation = nonNegativeOrNull(weather.precipitationMm);
  const probability = percentOrNull(weather.precipitationProbabilityPercent);
  const visibility = nonNegativeOrNull(weather.visibilityMeters);
  const uvIndex = nonNegativeOrNull(weather.uvIndex);
  const next6Hours = nonNegativeOrNull(weather.next6HoursPrecipitationMm);
  const condition = typeof weather.condition === "string" && weather.condition.trim() ? weather.condition.trim() : null;
  if (
    temperature === null && wind === null && gust === null && precipitation === null && probability === null
    && visibility === null && uvIndex === null && next6Hours === null && condition === null
  ) return undefined;
  return {
    temperatureCelsius: temperature,
    windSpeedKph: wind,
    gustSpeedKph: gust,
    precipitationMm: precipitation,
    precipitationProbabilityPercent: probability,
    visibilityMeters: visibility,
    uvIndex,
    next6HoursPrecipitationMm: next6Hours,
    condition,
    cached: weather.cached === true,
    timestamp: validTimestamp(weather.timestamp, fallbackTimestamp),
  };
};

const mapAlerts = (alerts: readonly TheraAlertState[] | undefined, fallbackTimestamp: string): readonly AlertState[] | undefined => {
  if (alerts === undefined) return undefined;
  return alerts.map((alert) => ({
    level: alert.level,
    code: alert.code,
    message: alert.message,
    timestamp: validTimestamp(alert.timestamp, fallbackTimestamp),
  }));
};

export class MountainAIAdapter {
  toMountainContext(snapshot: TheraRuntimeSnapshot): MountainContext {
    const gps = mapGps(snapshot.gps, snapshot.timestamp);
    const route = mapRoute(snapshot.route, snapshot.timestamp);
    const weather = mapWeather(snapshot.weather, snapshot.timestamp);
    const alerts = mapAlerts(snapshot.alerts, snapshot.timestamp);
    return {
      timestamp: snapshot.timestamp,
      offline: snapshot.networkState === "OFFLINE",
      ...(gps ? { gps } : {}),
      ...(route ? { route } : {}),
      ...(weather ? { weather } : {}),
      ...(alerts !== undefined ? { alerts } : {}),
    };
  }
}
